import { Search, Presentation, ShieldCheck, Building2, BarChart3, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";

const SERVICES = [
  {
    testId: "service-card-csr-matching",
    icon: Search,
    title: "CSR Mandate Matching & Discovery",
    desc: "We continuously track live CSR budgets, sector mandates, and geographic priorities of corporates across industries — then match them against your NGO's programs so you only pitch where you can win.",
    span: "lg:col-span-3",
  },
  {
    testId: "service-card-pitch-deck",
    icon: Presentation,
    title: "Boardroom Pitch Decks & Dossiers",
    desc: "Investor-grade pitch books, impact logic models, and budget frameworks that speak the language of CSR committees and board members.",
    span: "lg:col-span-3",
  },
  {
    testId: "service-card-compliance",
    icon: ShieldCheck,
    title: "80G / 12A / CSR-1 Compliance Packaging",
    desc: "Complete registration, audit trail, and documentation readiness so your NGO clears corporate due diligence on the first pass.",
    span: "lg:col-span-2",
  },
  {
    testId: "service-card-corporate-liaison",
    icon: Building2,
    title: "Corporate Liaison & Disbursement",
    desc: "We sit in the boardroom for you — negotiating MoUs, milestone schedules, and tranche-wise fund disbursement until money reaches your account.",
    span: "lg:col-span-2",
  },
  {
    testId: "service-card-impact-reporting",
    icon: BarChart3,
    title: "Post-Grant Impact M&E Reporting",
    desc: "Quarterly utilization certificates, beneficiary data, and impact reports that turn one-time grants into multi-year renewals.",
    span: "lg:col-span-2",
  },
];

export default function Services() {
  return (
    <section id="services" className="bg-white py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="max-w-2xl">
          <p className="font-mono-alt mb-4 text-xs font-semibold tracking-[0.35em] text-[#E8862A]">01 / WHAT WE DO</p>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#C7DAF5] bg-[#EAF1FC] px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#0E2A6B]">
            About & Services
          </span>
          <h2 className="mt-5 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
            The CSR Bridge Engine —{" "}
            <span className="font-display italic text-[#0E2A6B]">end-to-end fundraising support</span>
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600">
            Most NGOs lose corporate grants not because their work lacks impact, but because their
            paperwork, pitch, and positioning don't survive due diligence. We fix all three.
          </p>
        </div>

        <Reveal className="mt-12 grid gap-5 lg:grid-cols-6" delay={0.1}>
          <div className="relative min-h-[320px] overflow-hidden rounded-3xl bg-gradient-to-br from-[#0E2A6B] via-[#12306E] to-[#0A1A3F] lg:col-span-2 lg:row-span-2">
            <svg className="absolute inset-0 h-full w-full opacity-60" viewBox="0 0 400 560" fill="none" preserveAspectRatio="xMidYMid slice">
              <circle cx="200" cy="240" r="150" stroke="white" strokeOpacity="0.15" strokeWidth="1.5" strokeDasharray="4 8" />
              <circle cx="200" cy="240" r="100" stroke="white" strokeOpacity="0.2" strokeWidth="1.5" strokeDasharray="4 8" />
              <path d="M40 420 C 140 360, 240 280, 370 120" stroke="#2E6BE6" strokeWidth="7" strokeLinecap="round" />
              <polygon points="370,120 344,124 364,146" fill="#2E6BE6" />
              <circle cx="80" cy="392" r="13" fill="#E8862A" />
              <circle cx="205" cy="255" r="10" fill="#FFFFFF" />
              <circle cx="356" cy="132" r="11" fill="#2E9E44" />
            </svg>
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-white/15 bg-white/10 p-5 backdrop-blur-md">
              <Presentation className="h-10 w-10 text-white" />
            </div>
            <div className="absolute bottom-0 p-6">
              <p className="text-lg font-bold text-white">We represent you in the boardroom</p>
              <p className="mt-1 text-sm text-white/70">
                Direct access to CSR heads, foundation trustees, and ESG committees.
              </p>
            </div>
          </div>

          {SERVICES.map((s) => (
            <div
              key={s.testId}
              data-testid={s.testId}
              className={`group rounded-3xl border border-[#E6E9E6] bg-[#FBFBF9] p-6 transition-all hover:-translate-y-1 hover:border-[#3D63B8]/40 hover:shadow-xl ${s.span}`}
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#EAF1FC] text-[#0E2A6B] transition-colors group-hover:bg-[#0E2A6B] group-hover:text-white">
                <s.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 text-lg font-semibold text-slate-900">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
            </div>
          ))}
        </Reveal>

        <div className="mt-10 flex flex-wrap items-center gap-3 rounded-2xl bg-[#F4F3EE] px-6 py-5">
          <ArrowRight className="h-5 w-5 shrink-0 text-[#E8862A]" />
          <p className="text-sm text-slate-700">
            <span className="font-semibold text-slate-900">How engagement works:</span> free
            eligibility audit → association plan selection → dossier build → corporate outreach →
            grant disbursement → renewal reporting. No upfront risk, milestone-aligned.
          </p>
        </div>
      </div>
    </section>
  );
}
